/**
 * useLeadProducts
 * 
 * Hook para gerenciar produtos associados a uma negociação.
 * Permite adicionar, remover e atualizar produtos com recalculo automático de valor.
 * 
 * Princípios aplicados:
 * - Repository Pattern: Usa SupabaseOpportunityProductRepository
 * - Single Responsibility: Apenas gestão de produtos do lead
 */

import { useState, useEffect, useCallback } from 'react';
import { Lead } from '../../../types';
import { SupabaseOpportunityProductRepository, OpportunityProduct } from '../../../infrastructure/repositories/SupabaseOpportunityProductRepository';
import { useProducts } from '../../products/hooks/useProducts';
import { notify } from '../../../utils/toast';
import { createModuleLogger } from '../../../utils/logger';

const log = createModuleLogger('[LeadProducts]');

export const useLeadProducts = (lead: Lead, onUpdateLeadValue: (newValue: number) => void) => {
    const [opportunityProducts, setOpportunityProducts] = useState<OpportunityProduct[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const { products: availableProducts, createProduct, refreshProducts } = useProducts();

    const fetchProducts = useCallback(async () => {
        // ... (resto do fetchProducts)
        if (!lead?.id) return;
        setIsLoading(true);
        try {
            const data = await SupabaseOpportunityProductRepository.getByLeadId(lead.id);
            setOpportunityProducts(data);
        } catch (error) {
            log.error('Erro ao buscar produtos da oportunidade:', error);
            notify.error('Erro ao carregar produtos');
        } finally {
            setIsLoading(false);
        }
    }, [lead?.id]);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    // Recalcula valor total e atualiza lead se necessário
    const updateLeadValue = useCallback((currentProducts: OpportunityProduct[]) => {
        const total = currentProducts.reduce((acc, curr) => acc + (curr.total_price || (curr.quantity * curr.unit_price)), 0);

        // Só atualiza se o valor for diferente (evita loop, mas lead.value pode ter sido editado manualmente)
        // O usuário disse "os valores são somada no campo, mas que posso editar o valor caso queira".
        // Isso sugere que a soma dos produtos é uma *sugestão* ou *valor base*.
        // Se eu forçar updateLeadValue sempre que produtos mudarem, eu sobrescrevo edição manual?
        // Sim. Mas geralmente "Adicionar produto" é uma ação explícita que deve impactar o valor.
        // Se o usuário editou manualmente para 500, e adiciona um produto de 100, ele espera 600 ou 100? Espera 600 se fosse incremental, mas aqui é soma de produtos.
        // Se a lógica é "Soma dos produtos", então o valor do lead DEVE SER a soma dos produtos.
        // Se ele quiser editar, ele edita o preço UNITÁRIO do produto na lista.
        // Vou assumir que ao mexer nos produtos, o valor do lead é sobrescrito pela soma.

        if (Math.abs(total - lead.value) > 0.01) { // Floating point check
            onUpdateLeadValue(total);
        }
    }, [lead.value, onUpdateLeadValue]);

    const addProduct = async (productId: string | null, name: string, price: number, quantity: number) => {
        try {
            // Verifica se o produto já existe na lista (apenas para produtos cadastrados com ID)
            const existingProduct = productId
                ? opportunityProducts.find(p => p.product_id === productId)
                : null;

            if (existingProduct) {
                // Se existe, apenas incrementa a quantidade
                await updateProductQuantity(existingProduct.id, existingProduct.quantity + quantity);
                notify.success('Quantidade do produto atualizada');
            } else {
                // Se não existe, cria novo registro
                const newProduct = await SupabaseOpportunityProductRepository.add({
                    opportunity_id: lead.id,
                    product_id: productId,
                    name,
                    unit_price: price,
                    quantity
                });

                const updatedList = [...opportunityProducts, newProduct];
                setOpportunityProducts(updatedList);
                updateLeadValue(updatedList);
                notify.success('Produto adicionado');
            }
        } catch (error) {
            log.error('Erro ao adicionar produto:', error);
            notify.error('Erro ao adicionar produto');
        }
    };

    const removeProduct = async (id: string) => {
        try {
            await SupabaseOpportunityProductRepository.remove(id);
            const updatedList = opportunityProducts.filter(p => p.id !== id);
            setOpportunityProducts(updatedList);
            updateLeadValue(updatedList);
            notify.success('Produto removido');
        } catch (error) {
            log.error('Erro ao remover produto:', error);
            notify.error('Erro ao remover produto');
        }
    };

    const updateProductPrice = async (id: string, newPrice: number) => {
        try {
            await SupabaseOpportunityProductRepository.update(id, { unit_price: newPrice });
            const updatedList = opportunityProducts.map(p =>
                p.id === id ? { ...p, unit_price: newPrice, total_price: newPrice * p.quantity } : p
            );
            setOpportunityProducts(updatedList);
            updateLeadValue(updatedList);
        } catch (error) {
            log.error('Erro ao atualizar preço:', error);
            notify.error('Erro ao atualizar preço');
        }
    };

    const updateProductQuantity = async (id: string, newQuantity: number) => {
        try {
            await SupabaseOpportunityProductRepository.update(id, { quantity: newQuantity });
            const updatedList = opportunityProducts.map(p =>
                p.id === id ? { ...p, quantity: newQuantity, total_price: p.unit_price * newQuantity } : p
            );
            setOpportunityProducts(updatedList);
            updateLeadValue(updatedList);
        } catch (error) {
            log.error('Erro ao atualizar quantidade:', error);
            notify.error('Erro ao atualizar quantidade');
        }
    };

    return {
        opportunityProducts,
        isLoading,
        availableProducts,
        addProduct,
        removeProduct,
        updateProductPrice,
        updateProductQuantity,
        createProduct,
        refreshProducts
    };
};
